import React, { useState } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { useQuery, useLazyQuery } from '@apollo/client';
import { eesRequirementsCountries } from 'constants/eesRequirementsCountries';
import { useForm, FormProvider } from 'react-hook-form';
import { ConfirmButton } from 'components/ui/Button';
import Select from 'components/ui/Select';
import { BorderControlRequirementResults } from './BorderControlRequirementResults';
import { localeToLanguage } from 'utils/locale';
import { LONDON_UIC } from 'constants/directEurostarStations';
import AgentPortalError from 'errors/AgentPortalError';

import { StyledRow, StyledColumn, StyledLabel } from 'pages/CreateVoucher/CreateVoucher.styles';

import { StyledFormWrapper, StyledFormItems, ErrorMessage, StyledFlex, StyledFromTo } from './BorderControlRequirement.styles';

import { StyledForm } from 'pages/BookingDetails/CaptureTravelDocuments/CaptureDetailForm.styles';
import { GET_STATIONS, GET_BORDER_CONTROL_REQUIREMENTS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { Page, Column, Row } from 'components/shared/Layout';
import { getStationDetails, buildBorderData } from './BordercontrolRequirements.utils';

const RETURN_KEY = 'Return';
const YES_KEY = 'Yes';

const BorderControlRequirements = ({ intl: { formatMessage, locale } }) => {
  const language = localeToLanguage(locale);
  const [borderData, setBorderData] = useState(null);

  const [fetchRequirements, { loading: fetchRequirementsLoading, error: fetchRequirementsError }] = useLazyQuery(
    GET_BORDER_CONTROL_REQUIREMENTS,
    {
      onCompleted: (data) => data?.borderControlRequirements && setBorderData(buildBorderData(data.borderControlRequirements)),
      fetchPolicy: 'no-cache'
    }
  );

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { register, handleSubmit, formState, getValues, setValue, clearErrors } = formMethods;

  const { errors } = formState;

  const { data: allStationsData, loading: allStationsLoading, error: allStationsError } = useQuery(GET_STATIONS);
  const allStations = allStationsData?.stations;

  const onSubmit = async (data) => {
    const { from, to, countryOfIssue, tripRestriction, ukResidence, swissResidence } = data;

    await fetchRequirements({
      variables: {
        market: language === 'EN' ? 'UK' : language,
        language,
        origin: getStationDetails({ station: from, stations: allStations }),
        destination: getStationDetails({ station: to, stations: allStations }),
        document: countryOfIssue,
        isReturnJourney: tripRestriction === RETURN_KEY,
        isLongTermStayOrResidencyUk: ukResidence === YES_KEY ? true : false,
        isLongTermStayOrResidencyEeaSwiss: swissResidence === YES_KEY ? true : false
      }
    });
  };

  const handleStationChange = (direction, value) => {
    if (value !== LONDON_UIC) {
      clearErrors('from');
      clearErrors('to');

      if (direction === 'from') return setValue('to', LONDON_UIC);
      return setValue('from', LONDON_UIC);
    }

    clearErrors(direction);
  };

  if (allStationsError || fetchRequirementsError) {
    const error = allStationsError || fetchRequirementsError;
    throw new AgentPortalError(error);
  }

  return (
    <>
      {allStationsLoading ? (
        <Row span={16} height="100vh">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      ) : (
        <FormProvider {...formMethods}>
          <Page columns={20}>
            <Column span={18} start={2}>
              <Row>
                <h1>
                  <FormattedMessage id="ees.entryExitCta" />
                </h1>
              </Row>
              <Row>
                <h4>
                  <FormattedMessage id="ees.entryExitIntro" />
                </h4>
              </Row>

              <StyledForm data-testid="check-ees" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <StyledRow columns={12}>
                    <Row>
                      <StyledColumn alignItems="flex-start" flexDirection="column" span={3}>
                        <StyledLabel htmlFor="tripRestriction">
                          <FormattedMessage id="ees.returnOneWay" />
                        </StyledLabel>
                        <StyledFormItems>
                          <Select
                            data-testid="tripRestriction"
                            data-cy="trips-restrictions"
                            width="100%"
                            {...register('tripRestriction', { required: true })}
                            id="tripRestriction"
                            defaultValue={''}
                          >
                            <option disabled value="">
                              {formatMessage({ id: `ees.Select` })}
                            </option>

                            {[
                              { value: 'Return', name: 'returnJourney' },
                              { value: 'One-way', name: 'oneWayJourney' }
                            ].map((item, index) => (
                              <option key={`${item}--${index}`} value={item.value}>
                                {formatMessage({ id: `generateDiscountCode.${item.name}` })}
                              </option>
                            ))}
                          </Select>

                          {errors.tripRestriction && (
                            <ErrorMessage>
                              <FormattedMessage id="ees.errorGeneric" />
                            </ErrorMessage>
                          )}
                        </StyledFormItems>
                      </StyledColumn>

                      <StyledColumn alignItems="flex-start" flexDirection="column" span={8} start={5}>
                        <StyledLabel htmlFor="from">
                          <FormattedMessage id="ees.FromTo" />
                        </StyledLabel>

                        <StyledFlex>
                          <StyledFormItems>
                            <Select
                              data-testid="from"
                              data-cy="from"
                              width="100%"
                              {...register('from', { required: true })}
                              id="from"
                              defaultValue={''}
                              onChange={(e) => handleStationChange('from', e.target.value)}
                            >
                              <option disabled value="">
                                {formatMessage({ id: `ees.Select` })}
                              </option>

                              {allStations.map(({ name, uic }, index) => (
                                <option key={`${name}--${index}`} value={uic}>
                                  {name}
                                </option>
                              ))}
                            </Select>

                            {errors.from && (
                              <ErrorMessage>
                                <FormattedMessage id="ees.errorDepartureStation" />
                              </ErrorMessage>
                            )}
                          </StyledFormItems>

                          <StyledFromTo>{`->`}</StyledFromTo>

                          <StyledFormItems>
                            <Select
                              data-testid="to"
                              data-cy="to"
                              width="100%"
                              {...register('to', { required: true })}
                              id="to"
                              defaultValue={''}
                              onChange={(e) => handleStationChange('to', e.target.value)}
                            >
                              <option disabled value="">
                                {formatMessage({ id: `ees.Select` })}
                              </option>

                              {allStations.map(({ name, uic }, index) => (
                                <option key={`${name}--${index}`} value={uic}>
                                  {name}
                                </option>
                              ))}
                            </Select>

                            {errors.to && (
                              <ErrorMessage>
                                <FormattedMessage id="ees.errorDestinationStation" />
                              </ErrorMessage>
                            )}
                          </StyledFormItems>
                        </StyledFlex>
                      </StyledColumn>
                    </Row>

                    <Row>
                      <StyledFormWrapper>
                        <StyledLabel mandatory htmlFor="countryOfIssue">
                          <FormattedMessage id="ees.issuingCountry" />
                        </StyledLabel>
                        <StyledFormItems>
                          <Select
                            {...register('countryOfIssue', {
                              required: true
                            })}
                            id="countryOfIssue"
                            data-cy="countryOfIssue"
                            defaultValue={''}
                          >
                            <option disabled value="">
                              {formatMessage({ id: `ees.Select` })}
                            </option>

                            {eesRequirementsCountries.map(({ value, name }) => (
                              <option key={value} value={value}>
                                {name}
                              </option>
                            ))}
                          </Select>

                          {errors.countryOfIssue && (
                            <ErrorMessage>
                              <FormattedMessage id="ees.errorCountry" />
                            </ErrorMessage>
                          )}
                        </StyledFormItems>
                      </StyledFormWrapper>
                    </Row>

                    <Row>
                      <StyledFormWrapper>
                        <StyledLabel htmlFor="ukResidence">
                          <FormattedMessage id="ees.ukResidence" />
                        </StyledLabel>
                        <StyledFormItems>
                          <Select
                            data-testid="ukResidence"
                            data-cy="ukResidence"
                            width="100%"
                            {...register('ukResidence', { required: true })}
                            id="ukResidence"
                            defaultValue={''}
                          >
                            <option disabled value="">
                              {formatMessage({ id: `ees.Select` })}
                            </option>

                            {['Yes', 'No'].map((item, index) => (
                              <option key={`${item}--${index}`} value={item}>
                                {formatMessage({ id: `interaction.${item.toLowerCase()}` })}
                              </option>
                            ))}
                          </Select>

                          {errors.ukResidence && (
                            <ErrorMessage>
                              <FormattedMessage id="ees.errorGeneric" />
                            </ErrorMessage>
                          )}
                        </StyledFormItems>
                      </StyledFormWrapper>
                    </Row>

                    <Row>
                      <StyledFormWrapper>
                        <StyledLabel htmlFor="swissResidence">
                          <FormattedMessage id="ees.swissResidence" />
                        </StyledLabel>
                        <StyledFormItems>
                          <Select
                            data-testid="swissResidence"
                            data-cy="swissResidence"
                            width="100%"
                            {...register('swissResidence', { required: true })}
                            id="swissResidence"
                            defaultValue={''}
                          >
                            <option disabled value="">
                              {formatMessage({ id: `ees.Select` })}
                            </option>

                            {['Yes', 'No'].map((item, index) => (
                              <option key={`${item}--${index}`} value={item}>
                                {formatMessage({ id: `interaction.${item.toLowerCase()}` })}
                              </option>
                            ))}
                          </Select>

                          {errors.swissResidence && (
                            <ErrorMessage>
                              <FormattedMessage id="ees.errorGeneric" />
                            </ErrorMessage>
                          )}
                        </StyledFormItems>
                      </StyledFormWrapper>
                    </Row>
                  </StyledRow>

                  <ConfirmButton
                    type="submit"
                    data-cy="check-requirements"
                    loading={fetchRequirementsLoading}
                    disabled={fetchRequirementsLoading}
                  >
                    <FormattedMessage id="ees.checkRequirements" />
                  </ConfirmButton>
                </div>
              </StyledForm>
            </Column>
            <Column span={18} start={2}>
              {fetchRequirementsLoading ? (
                <Column justifyContent="center" alignItems="center">
                  <Spinner />
                </Column>
              ) : (
                <BorderControlRequirementResults
                  data={borderData}
                  isReturnJourney={getValues('tripRestriction') === RETURN_KEY}
                  from={getValues('from')}
                  to={getValues('to')}
                  allStations={allStations}
                />
              )}
            </Column>
          </Page>
        </FormProvider>
      )}
    </>
  );
};

BorderControlRequirements.propTypes = {
  intl: intlShape
};

export default injectIntl(BorderControlRequirements);
